import { FIELDS } from 'modules/cm/fields/types';
import { FIELDS as CURRENT_INCOME_FIELDS } from 'modules/cm/domain/Case/Applicant/Income/Current/Current';
import { FIELDS as FUTURE_INCOME_FIELDS } from 'modules/cm/domain/Case/Applicant/Income/Future/Future';
import { CaseError as APICaseError } from '../caseError';
import { CaseError as DomainCaseError } from 'modules/cm/domain/Case/Error';

const mapCaseError = ({ case: apiError }: APICaseError): DomainCaseError => {
  const mapProperty = (propertyKey: keyof Omit<APICaseError['case']['property'], 'occupants'>): string | undefined => {
    const property = apiError.property;
    return property && propertyKey in property ? property[propertyKey] : undefined;
  };

  const mapApplicantCurrentAddress = (applicant: NonNullable<APICaseError['case']['applicant1']>) => (
    key: keyof NonNullable<APICaseError['case']['applicant1']['currentAddress']>,
  ): string | undefined => {
    const obj = applicant.currentAddress;
    return obj && key in obj
      ? obj[key as keyof NonNullable<APICaseError['case']['applicant1']>['currentAddress']]
      : undefined;
  };

  const mapApplicantPowerOfAttorney = (applicant: NonNullable<APICaseError['case']['applicant1']>) => (
    key: keyof NonNullable<APICaseError['case']['applicant1']['powerOfAttorney']>,
  ): string | undefined => {
    const obj = applicant.powerOfAttorney;
    return obj && key in obj
      ? obj[key as keyof NonNullable<APICaseError['case']['applicant1']>['powerOfAttorney']]
      : undefined;
  };

  return {
    applicants: [apiError.applicant1, apiError.applicant2].map((applicant) => {
      const mapApplicantCurrentAddressProperty = mapApplicantCurrentAddress(applicant);
      const mapApplicantPowerOfAttorneyProperty = mapApplicantPowerOfAttorney(applicant);

      return {
        [FIELDS.ID]: applicant.id,
        [FIELDS.APPLICANT_NAME]: applicant.firstName,
        [FIELDS.APPLICANT_SURNAME]: applicant.lastName,
        [FIELDS.APPLICANT_TITLE]: applicant.title,
        [FIELDS.ASSETS_PREFIX]: (applicant.assets || []).map((api) => ({
          flatNumber: api.flatNumber,
          buildingName: api.buildingName,
          buildingNumber: api.buildingNumber,
          street: api.street,
          city: api.city,
          description: api.description,
          id: api.id,
          outstandingMortgageBalance: api.outstandingMortgageBalance,
          postcode: api.postcode,
          type: api.type,
          value: api.value,
          willBeUsedToPayLoan: api.willBeUsedToPayLoan,
        })),
        [FIELDS.APPLICANT_MIDDLE_NAME]: applicant.middleName,
        [FIELDS.APPLICANT_PREVIOUS_NAME]: applicant.previousName,
        [FIELDS.APPLICANT_PROOF_OF_ID]: applicant.proofOfId,
        [FIELDS.APPLICANT_DATE_OF_BIRTH]: applicant.dateOfBirth,
        [FIELDS.APPLICANT_GENDER]: applicant.gender,
        [FIELDS.APPLICANT_MARITAL_STATUS]: applicant.maritalStatus,
        [FIELDS.APPLICANT_CURRENT_ADDRESS]: {
          city: mapApplicantCurrentAddressProperty('city'),
          id: mapApplicantCurrentAddressProperty('id'),
          flatNumber: mapApplicantCurrentAddressProperty('flatNumber'),
          buildingName: mapApplicantCurrentAddressProperty('buildingName'),
          buildingNumber: mapApplicantCurrentAddressProperty('buildingNumber'),
          street: mapApplicantCurrentAddressProperty('street'),
          movedInDate: mapApplicantCurrentAddressProperty('movedInDate'),
          postcode: mapApplicantCurrentAddressProperty('postcode'),
          proofOfAddress: mapApplicantCurrentAddressProperty('proofOfAddress'),
        },
        [FIELDS.APPLICANT_PREVIOUS_ADDRESSES]: (applicant.previousAddresses || []).map((api) => ({
          id: api.id,
          movedInDate: api.movedInDate,
          flatNumber: api.flatNumber,
          buildingName: api.buildingName,
          buildingNumber: api.buildingNumber,
          street: api.street,
          city: api.city,
          postcode: api.postcode,
        })),
        [FIELDS.LUMP_SUM_DRAWDOWN]: applicant.lumpSumDrawdown,
        [FIELDS.REPAYMENT_LUMP_SUM]: applicant.repaymentLumpSum,
        [FIELDS.APPLICANT_NATIONALITY]: applicant.nationality,
        [FIELDS.APPLICANT_IS_UK_RESIDENT]: applicant.isUkResident,
        [FIELDS.APPLICANT_HAS_INDEFINITE_LEAVE_TO_REMAIN]: applicant.hasIndefiniteLeaveToRemain,
        [FIELDS.APPLICANT_PHONE_HOME]: applicant.phoneHome,
        [FIELDS.APPLICANT_IS_PHONE_HOME_PEPPER]: applicant.isPhoneWorkPepper,
        [FIELDS.APPLICANT_PHONE_MOBILE]: applicant.phoneMobile,
        [FIELDS.APPLICANT_IS_PHONE_MOBILE_PEPPER]: applicant.isPhoneMobilePepper,
        [FIELDS.APPLICANT_PHONE_WORK]: applicant.phoneWork,
        [FIELDS.APPLICANT_IS_PHONE_WORK_PEPPER]: applicant.isPhoneWorkPepper,
        [FIELDS.APPLICANT_EMAIL]: applicant.email,
        [FIELDS.APPLICANT_IS_EMAIL_PEPPER]: applicant.isEmailPepper,
        [FIELDS.APPLICANT_ADDITIONAL_EMAIL]: applicant.additionalEmail,
        [FIELDS.APPLICANT_ADDITIONAL_EMAIL_PEPPER]: applicant.isAdditionalEmailPepper,
        [FIELDS.APPLICANT_IS_CRIMINAL]: applicant.isCriminal,
        [FIELDS.APPLICANT_CRIMINAL_CONVICTIONS_DETAIL]: applicant.criminalConvictionsDetails,
        [FIELDS.APPLICANT_HAS_ADVERSE_CREDIT]: applicant.hasAdverseCredit,
        [FIELDS.APPLICANT_ADVERSE_CREDIT_DETAILS]: applicant.adverseCreditDetails,
        [FIELDS.APPLICANT_BANKRUPTCY_AMOUNT]: applicant.bankruptcyAmount,
        [FIELDS.APPLICANT_BANKRUPTCY_LATEST_DATE]: applicant.bankruptcyLatestDate,
        [FIELDS.APPLICANT_CCJ_AMOUNT]: applicant.ccjAmount,
        [FIELDS.APPLICANT_CCJ_LATEST_DATE]: applicant.ccjLatestDate,
        [FIELDS.APPLICANT_DEFAULT_AMOUNT]: applicant.defaultAmount,
        [FIELDS.APPLICANT_DEFAULT_LAST_DATE]: applicant.defaultLatestDate,
        [FIELDS.APPLICANT_MORTGAGE_ARREARS_AMOUNT]: applicant.mortgageArrearsAmount,
        [FIELDS.APPLICANT_MORTGAGE_ARREARS_LATEST_DATE]: applicant.mortgageArrearsLatestDate,
        [FIELDS.APPLICANT_POWER_OF_ATTORNEY_CUSTOMISED_SERVICES_REQUEST]: applicant.customisedServicesRequest,
        [FIELDS.APPLICANT_POWER_OF_ATTORNEY_HAS_CUSTOMISED_SERVICES]: applicant.hasCustomisedServices,
        [FIELDS.APPLICANT_POWER_OF_ATTORNEY]: {
          flatNumber: mapApplicantPowerOfAttorneyProperty('flatNumber'),
          buildingNumber: mapApplicantPowerOfAttorneyProperty('buildingNumber'),
          buildingName: mapApplicantPowerOfAttorneyProperty('buildingName'),
          street: mapApplicantPowerOfAttorneyProperty('street'),
          city: mapApplicantPowerOfAttorneyProperty('city'),
          email: mapApplicantPowerOfAttorneyProperty('email'),
          id: mapApplicantPowerOfAttorneyProperty('id'),
          name: mapApplicantPowerOfAttorneyProperty('name'),
          phoneNumber: mapApplicantPowerOfAttorneyProperty('phoneNumber'),
          postcode: mapApplicantPowerOfAttorneyProperty('postcode'),
        },
        [FIELDS.EXPENDITURES_PREFIX]: (applicant.expenditures || []).map((api) => ({
          amount: api.amount,
          balance: api.balance,
          creditLimit: api.creditLimit,
          description: api.description,
          endDate: api.endDate,
          frequency: api.frequency,
          id: api.id,
          totalNumberOfProperties: api.totalNumberOfProperties,
          type: api.type,
          willBeClearedBeforeLoan: api.willBeClearedBeforeLoan,
          willBeClearedByLoan: api.willBeClearedByLoan,
        })),
        [FIELDS.APPLICANT_NUMBER_OF_DEPENDANTS]: applicant.numberOfDependants,

        // Current pension
        [FIELDS.CURRENT_STATE_PENSION]: applicant.currentStatePension,
        [FIELDS.CURRENT_DB_NAME_OF_SCHEME]: applicant.currentDbNameOfScheme,
        [FIELDS.CURRENT_DB_ANNUAL_INCOME]: applicant.currentDbAnnualIncome,
        [FIELDS.CURRENT_DB_PERCENT_PASSED_ON_DEATH]: applicant.currentDbPercentPassedOnDeath,
        [FIELDS.CURRENT_DC_NAME_OF_SCHEME]: applicant.currentDcNameOfScheme,
        [FIELDS.CURRENT_DC_CURRENT_VALUE]: applicant.currentDcCurrentValue,
        [FIELDS.CURRENT_DC_PERCENT_PASSED_ON_DEATH]: applicant.currentDcPercentPassedOnDeath,
        [FIELDS.CURRENT_DC_ANNUAL_INCOME_DRAWN]: applicant.currentDcAnnualIncomeDrawn,
        [FIELDS.CURRENT_ANNUITY_NAME_OF_ANNUITY_PROVIDER]: applicant.currentAnnuityNameOfAnnuityProvider,
        [FIELDS.CURRENT_ANNUITY_GROSS_ANNUAL_INCOME]: applicant.currentAnnuityGrossAnnualIncome,
        [FIELDS.CURRENT_ANNUITY_PERCENT_PASSED_ON_DEATH]: applicant.currentAnnuityPercentPassedOnDeath,

        // Future pension
        [FIELDS.FUTURE_STATE_PENSION]: applicant.futureStatePension,
        [FIELDS.FUTURE_PENSION_DEFINED_BENEFITS_PREFIX]: (applicant.futurePensionDefinedBenefits || []).map((api) => ({
          id: api.id,
          nameOfScheme: api.nameOfScheme,
          annualIncome: api.annualIncome,
          startDate: api.startDate,
          percentageValueOfPassedOnDeath: api.percentageValueOfPassedOnDeath,
          repaymentLumpSum: api.repaymentLumpSum,
        })),
        [FIELDS.FUTURE_PENSION_DEFINED_CONTRIBUTIONS_PREFIX]: (applicant.futurePensionDefinedContributions || []).map(
          (api) => ({
            id: api.id,
            [FIELDS.NAME_OF_SCHEME]: api.nameOfScheme,
            [FIELDS.CURRENT_VALUE]: api.currentValue,
            [FIELDS.START_DATE]: api.startDate,
            [FIELDS.ONGOING_MONTHLY_CONTRIBUTIONS]: api.ongoingMonthlyContributions,
            [FIELDS.PERCENTAGE_VALUE_OF_PASSED_ON_DEATH]: api.percentageValueOfPassedOnDeath,
            [FIELDS.FUTURE_ANNUAL_INCOME_DRAWN]: api.annualIncomeDrawn,
            [FIELDS.USE_OF_FUND]: api.useOfFund,
          }),
        ),
        [FIELDS.FUTURE_ANNUITY_PREFIX]:
          applicant.futurePensionAnnuity && applicant.futurePensionAnnuity.length > 0
            ? applicant.futurePensionAnnuity.map((api: typeof applicant.futurePensionAnnuity[0]) => ({
                id: api.id,
                [FIELDS.FUTURE_ANNUITY_NAME_OF_ANNUITY_PROVIDER]: api.annuityProvider,
                [FIELDS.FUTURE_ANNUITY_GROSS_ANNUAL_INCOME]: api.annuityGrossIncome,
                [FIELDS.FUTURE_ANNUITY_PERCENT_PASSED_ON_DEATH]: api.annuityPercentageToOtherInCaseOfDeath,
              }))
            : [],

        // Employed
        [FIELDS.APPLICANT_EMPLOYMENT_STATUS]: applicant.employmentStatus,
        [FIELDS.APPLICANT_EMPLOYED_JOB_TITLE]: applicant.employedJobTitle,
        [FIELDS.APPLICANT_EMPLOYED_COMPANY_NAME]: applicant.employedCompanyName,
        [FIELDS.APPLICANT_EMPLOYED_COMPANY_ADDRESS]: applicant.employedCompanyAddress,
        [FIELDS.APPLICANT_EMPLOYED_NATURE]: applicant.employedNature,
        [FIELDS.APPLICANT_EMPLOYED_START_DATE]: applicant.employedStartDate,
        [FIELDS.APPLICANT_EMPLOYED_ANTICIPATED_AGE]: applicant.employedAnticipatedAge,
        [FIELDS.APPLICANT_SELF_EMPLOYED_JOB_TITLE]: applicant.selfEmployedJobTitle,
        [FIELDS.APPLICANT_SELF_EMPLOYED_COMPANY_NAME]: applicant.selfEmployedCompanyName,
        [FIELDS.APPLICANT_SELF_EMPLOYED_COMPANY_ADDRESS]: applicant.selfEmployedCompanyAddress,
        [FIELDS.APPLICANT_SELF_EMPLOYED_NATURE]: applicant.selfEmployedNature,
        [FIELDS.APPLICANT_SELF_EMPLOYED_START_DATE]: applicant.selfEmployedStartDate,
        [FIELDS.APPLICANT_BUSINESS_TYPE]: applicant.businessType,
        [FIELDS.APPLICANT_SELF_EMPLOYED_ANTICIPATED_AGE]: applicant.selfEmployedAnticipatedAge,

        // Current Income
        [CURRENT_INCOME_FIELDS.EMPLOYED_TOTAL]: applicant.currentIncomeEmployedTotal,
        [CURRENT_INCOME_FIELDS.SELF_EMPLOYED_TOTAL]: applicant.currentIncomeSelfEmployedTotal,
        [CURRENT_INCOME_FIELDS.OTHER_TOTAL]: applicant.currentIncomeOtherTotal,
        [CURRENT_INCOME_FIELDS.TOTAL]: applicant.currentIncomeTotal,
        [CURRENT_INCOME_FIELDS.CURRENT_INCOME_PENSION]: applicant.currentIncomePensionIncome,
        [CURRENT_INCOME_FIELDS.BASIC_GROSS_ANNUAL_SALARY]: applicant.currentIncomeBasicGrossAnnualSalary,
        [CURRENT_INCOME_FIELDS.ANNUAL_BONUS]: applicant.currentIncomeAnnualBonus,
        [CURRENT_INCOME_FIELDS.HAS_ANNUAL_BONUS_GUARANTEED]: applicant.currentIncomeHasAnnualBonusGuaranteed,
        [CURRENT_INCOME_FIELDS.ANNUAL_COMMISSION]: applicant.currentIncomeAnnualCommission,
        [CURRENT_INCOME_FIELDS.HAS_COMMISSION_GUARANTEED]: applicant.currentIncomeHasCommissionGuaranteed,
        [CURRENT_INCOME_FIELDS.ANNUAL_OVERTIME]: applicant.currentIncomeAnnualOvertime,
        [CURRENT_INCOME_FIELDS.HAS_OVERTIME_GUARANTEED]: applicant.currentIncomeHasOvertimeGuaranteed,
        [CURRENT_INCOME_FIELDS.ANNUAL_SHIFT_ALLOWANCE]: applicant.currentIncomeAnnualShiftAllowance,
        [CURRENT_INCOME_FIELDS.HAS_SHIFT_ALLOWANCE_GUARANTEED]: applicant.currentIncomeHasShiftAllowanceGuaranteed,
        [CURRENT_INCOME_FIELDS.BUSINESS_TYPE]: applicant.currentIncomeBusinessType,
        [CURRENT_INCOME_FIELDS.PERCENTAGE_OF_BUSINESS_OWNED]: applicant.currentIncomePercentageOfBusinessOwned,
        [CURRENT_INCOME_FIELDS.LIMITED_COMPANY_SALARY]: applicant.currentIncomeLimitedCompanySalary,
        [CURRENT_INCOME_FIELDS.LIMITED_COMPANY_DIVIDENDS]: applicant.currentIncomeLimitedCompanyDividends,
        [CURRENT_INCOME_FIELDS.NET_PROFIT]: applicant.currentIncomeNetProfit,
        [CURRENT_INCOME_FIELDS.BUSINESS_TRADING_DATE]: applicant.currentIncomeBusinessTradingDate,
        [CURRENT_INCOME_FIELDS.INVESTMENT_PREFIX]: (applicant.currentIncomeInvestmentIncomes || []).map((api) => ({
          annualDividendIncome: api.annualDividendIncome,
          annualInvestmentIncome: api.annualDividendIncome,
          id: api.id,
          investmentPortfolioValue: api.investmentPortfolioValue,
        })),
        [CURRENT_INCOME_FIELDS.RENTAL_PREFIX]: (applicant.currentIncomeRentalIncomes || []).map((api) => ({
          grossAnnualRentalIncome: api.grossAnnualRentalIncome,
          id: api.id,
          mortgagePayment: api.mortgagePayment,
          noOfBtlsInPortfolio: api.noOfBtlsInPortfolio,
        })),
        [CURRENT_INCOME_FIELDS.FOSTERING_PREFIX]: (applicant.currentIncomeFosteringIncomes || []).map((api) => ({
          grossAnnualFosteringIncome: api.grossAnnualFosteringIncome,
          id: api.id,
        })),
        [CURRENT_INCOME_FIELDS.MAINTENANCE_PREFIX]: (applicant.currentIncomeMaintenanceIncomes || []).map((api) => ({
          grossAnnualMaintenanceIncome: api.grossAnnualMaintenanceIncome,
          hasMaintenanceGuaranteedOtherIncome: api.hasMaintenanceGuaranteedOtherIncome,
          id: api.id,
        })),
        [CURRENT_INCOME_FIELDS.BENEFIT_PREFIX]: (applicant.currentIncomeBenefitIncomes || []).map((api) => ({
          grossAnnualBenefitIncome: api.grossAnnualBenefitIncome,
          hasBenefitGuaranteedOtherIncome: api.hasBenefitGuaranteedOtherIncome,
          id: api.id,
        })),
        [CURRENT_INCOME_FIELDS.OTHER_PREFIX]: (applicant.currentIncomeOtherIncomes || []).map((api) => ({
          grossAnnualOtherIncome: api.grossAnnualOtherIncome,
          hasBonusGuaranteedOtherIncome: api.hasBonusGuaranteedOtherIncome,
          id: api.id,
        })),

        // Future Income
        [FUTURE_INCOME_FIELDS.MINIMUM_RETIRED_INCOME]: applicant.minimumRetiredIncome,
        [FUTURE_INCOME_FIELDS.FUTURE_INCOME_PENSION]: applicant.futureIncomePensionIncome,
        [FUTURE_INCOME_FIELDS.FUTURE_INCOME_TOTAL]: applicant.futureIncomeTotalIncome,
        [FUTURE_INCOME_FIELDS.EMPLOYED_ANTICIPATED_GROSS_ANNUAL_INCOME]: applicant.employedAnticipatedGrossAnnualIncome,
        [FUTURE_INCOME_FIELDS.EMPLOYED_ANTICIPATED_RETIREMENT_DATE]: applicant.plannedRetirementAge,
        [FUTURE_INCOME_FIELDS.SELF_EMPLOYED_ANTICIPATED_GROSS_ANNUAL_INCOME]: '',
        [FUTURE_INCOME_FIELDS.SELF_EMPLOYED_ANTICIPATED_RETIREMENT_DATE]: '',
        [FUTURE_INCOME_FIELDS.MINIMUM_TAXABLE_RETIRED_INCOME]: applicant.minimumTaxableRetiredIncome,
        [FUTURE_INCOME_FIELDS.HAS_TAXABLE_RETIRED_INCOME_CONTINUING]: applicant.hasTaxableRetiredIncomeContinuing,
        [FUTURE_INCOME_FIELDS.MINIMUM_TAX_FREE_RETIRED_INCOME]: applicant.minimumTaxFreeRetiredIncome,
        [FUTURE_INCOME_FIELDS.HAS_TAX_FREE_RETIRED_INCOME_CONTINUING]: applicant.hasTaxFreeRetiredIncomeContinuing,
        [FUTURE_INCOME_FIELDS.OTHER_INCOME_ANTICIPATED_GROSS_ANNUAL_INCOME]:
          applicant.otherIncomeAnticipatedGrossAnnualIncome,
        [FUTURE_INCOME_FIELDS.OTHER_INCOME_DETAILS]: applicant.otherIncomeDetails,
        [FUTURE_INCOME_FIELDS.MINIMUM_TAXABLE_RETIRED_INCOME_DETAILS]: applicant.minimumTaxableRetiredIncomeDetails,
        [FUTURE_INCOME_FIELDS.MINIMUM_TAX_FREE_RETIRED_INCOME_DETAILS]: applicant.minimumTaxFreeRetiredIncomeDetails,
      };
    }),

    assurancePolicies: (apiError.policies || []).map((api) => ({
      assuredPerson: api.assuredPerson,
      basisOfCover: api.basisOfCover,
      expiryDate: api.expiryDate,
      id: api.id,
      insuranceLumpSum: api.insuranceLumpSum,
      monthlyPremium: api.monthlyPremium,
      mortgageMonthlyPayments: api.mortgageMonthlyPayments,
      paymentsPerAnnum: api.paymentsPerAnnum,
      type: api.type,
    })),

    contact: {
      admin: {
        completionDate: apiError.completionDate,
        cotRecDate: apiError.cotRecDate,
        declineDate: apiError.declineDate,
        expectedMaturity: apiError.expectedMaturity,
        fundsReqDate: apiError.fundsReqDate,
        offerDate: apiError.offerDate,
        originatorId: apiError.originatorId,
        pepperAccountId: apiError.pepperAccountId,
        pepperUploadDate: apiError.pepperUploadDate,
        swapCost: apiError.swapCost,
      },
      adviserEmail: apiError.adviserEmail,
      adviserFee: apiError.adviserFee,
      adviserFeeLastPaymentMoment: apiError.adviserFeeLastPaymentMoment,
      adviserName: apiError.adviserName,
      adviserPhone: apiError.adviserPhone,
      firmAddress: apiError.firmAddress,
      firmFcaNumber: apiError.firmFcaNumber,
      firmName: apiError.firmName,
      networkFcaNumber: apiError.networkFcaNumber,
      solicitor: {
        solicitorFlatNumber: apiError.solicitorFlatNumber,
        solicitorBuildingNumber: apiError.solicitorBuildingNumber,
        solicitorBuildingName: apiError.solicitorBuildingName,
        solicitorStreet: apiError.solicitorStreet,
        solicitorCity: apiError.solicitorCity,
        solicitorContactNumber: apiError.solicitorContactNumber,
        solicitorEmail: apiError.solicitorEmail,
        solicitorName: apiError.solicitorName,
        solicitorPostcode: apiError.solicitorPostcode,
      },
    },
    loanDetails: {
      termLength: apiError.productTermLength,
      isPepperUploadApproved: apiError.isPepperUploadApproved,
      accountName: apiError.accountName,
      accountNumber: apiError.accountNumber,
      actualPropertyValue: apiError.actualPropertyValue,
      additionalDetails: apiError.additionalDetails,
      affordabilityResults: {
        buffer: apiError.afDisposableIncomeBuffer,
        income: apiError.afDisposableIncome,
        livingCosts: apiError.afLivingCosts,
        maxLoanPossible: apiError.afMaxLoanPossible,
        netCashFlow: apiError.afNetCashflow,
        stage: apiError.stage,
      },
      annualCouncilTax: apiError.annualCouncilTax,
      annualGroundRent: apiError.annualGroundRent,
      applicationType: apiError.applicationType,
      bankOrBuildingSocietyName: apiError.bankOrBuildingSocietyName,
      createdBy: apiError.createdBy,
      creditPolicyExemptionRationale: apiError.creditPolicyExemptionRationale,
      currentMortgageProvider: apiError.currentMortgageProvider,
      delegatedAuthorityLevel: apiError.delegatedAuthorityLevel,
      delphiScore: apiError.delphiScore,
      depositSourceBuilders: apiError.depositSourceBuilders,
      depositSourceDetails: apiError.depositSourceDetails,
      depositSourceEquityFromSale: apiError.depositSourceEquityFromSale,
      depositSourceGifts: apiError.depositSourceGifts,
      depositSourceOther: apiError.depositSourceOther,
      depositSourceSavings: apiError.depositSourceSavings,
      estimatedPropertyValue: apiError.estimatedPropertyValue,
      existingMortgageStatus: apiError.existingMortgageStatus,
      fcaLoanPurpose: apiError.fcaLoanPurpose,
      hasMarketingEmail: apiError.hasMarketingEmail,
      hasMarketingPhone: apiError.hasMarketingPhone,
      hasMarketingPost: apiError.hasMarketingPost,
      hasMarketingSms: apiError.hasMarketingSms,
      id: apiError.id,
      initialAssessmentApprovedBy: apiError.approvedBy,
      initialAssessmentApprovedDate: apiError.approvedDate,
      initialAssessmentBy: apiError.initialAssessmentBy,
      assessmentDate: apiError.assessmentDate,
      isBuyToLet: apiError.isBuyToLet,
      hasAdverseCredit: apiError.hasAdverseCredit,
      isCreditPolicyExemption: apiError.isCreditPolicyExemption,
      isFirstTimeBuyer: apiError.isFirstTimeBuyer,
      isPrivateSale: apiError.isPrivateSale,
      loanAmountPurchase: apiError.loanAmountPurchase,
      loanAmountRemortgage: apiError.loanAmountRemortgage,
      loanAssistingOthers: apiError.loanAssistingOthers,
      loanDebtConsolidation: apiError.loanDebtConsolidation,
      loanDiscretionarySpend: apiError.loanDiscretionarySpend,
      loanHomeImprovements: apiError.loanHomeImprovements,
      loanOtherPurposes: apiError.loanOtherPurposes,
      monthlyBasicSpend: apiError.monthlyBasicSpend,
      netLoan: apiError.netLoan,
      outstandingMortgageAmount: apiError.outstandingMortgageAmount,
      owner: apiError.assignedUserId,
      pepperPurposeOfTheLoan: apiError.pepperPurposeOfTheLoan,
      postcode: apiError.postcode,
      productCode: apiError.productCode,
      productFeePaymentMethod: apiError.productFeePaymentMethod,
      propertyPurchasePrice: apiError.propertyPurchasePrice,
      purposeOfCapitalRaise: apiError.purposeOfCapitalRaise,
      purposeOfTheLoan: apiError.purposeOfTheLoan,
      reinspectionFee: apiError.reinspectionFee,
      sortCode: apiError.sortCode,
      sourceOfDeposit: apiError.sourceOfDeposit,
      sourceOfFundsToClearOutstandingMortgage: apiError.sourceOfFundsToClearOutstandingMortgage,
      stage: apiError.stage,
      status: apiError.status,
      updatedAt: apiError.updatedAt,
      uwRecommendation: apiError.uwRecommendation,
      uwSpecialConditions: apiError.uwSpecialConditions,
      valuationFeePayment: apiError.valuationFeePayment,
    },
    property: {
      actualPropertyValue: mapProperty('actualPropertyValue'),
      city: mapProperty('city'),
      commercialPremisesDetails: mapProperty('commercialPremisesDetails'),
      commercialUsageDetails: mapProperty('commercialUsageDetails'),
      estimatedPropertyValue: mapProperty('estimatedPropertyValue'),
      floorNumber: mapProperty('floorNumber'),
      hasAgriculturalRestrictions: mapProperty('hasAgriculturalRestrictions'),
      hasCommercialUsageAbove25Percent: mapProperty('hasCommercialUsageAbove25Percent'),
      hasLiftAccess: mapProperty('hasLiftAccess'),
      hasOccupantsOver17: mapProperty('hasOccupantsOver17'),
      hasSpecialistReport: mapProperty('hasSpecialistReport'),
      inspectionDate: mapProperty('inspectionDate'),
      instructionDate: mapProperty('instructionDate'),
      isAboveCommercialPremises: mapProperty('isAboveCommercialPremises'),
      isExLocalAuthority: mapProperty('isExLocalAuthority'),
      isPropertyNewBuild: mapProperty('isPropertyNewBuild'),
      isRestrictedCovenant: mapProperty('isRestrictedCovenant'),
      isRetirementHome: mapProperty('isRetirementHome'),
      flatNumber: mapProperty('flatNumber'),
      buildingName: mapProperty('buildingName'),
      buildingNumber: mapProperty('buildingNumber'),
      street: mapProperty('street'),
      numberOfBedrooms: mapProperty('numberOfBedrooms'),
      numberOfFloorsInTheBuilding: mapProperty('numberOfFloorsInTheBuilding'),
      numberOfKitchens: mapProperty('numberOfKitchens'),
      occupants: (apiError.property ? apiError.property.occupants || [] : []).map((api) => ({
        dateOfBirth: api.dateOfBirth,
        fullName: api.fullName,
        id: api.id,
      })),
      postcode: mapProperty('postcode'),
      propertyConstruction: mapProperty('propertyConstruction'),
      propertyDetails: mapProperty('propertyDetails'),
      propertyPurchasePrice: mapProperty('propertyPurchasePrice'),
      propertyYearBuilt: mapProperty('propertyYearBuilt'),
      remainingYears: mapProperty('remainingYears'),
      tenureType: mapProperty('tenureType'),
      titleNumber: mapProperty('titleNumber'),
      type: mapProperty('type'),
      valuationType: mapProperty('valuationType'),
      warrantyProvider: mapProperty('warrantyProvider'),
    },
    repaymentStrategy: {
      repaymentStrategyTotal:
        'repaymentStrategy' in apiError ? apiError.repaymentStrategy.repaymentStrategyTotal : undefined,
    },
  };
};

export default mapCaseError;
